import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, RefreshCw, CheckSquare } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet, apiPost } from '../../hooks/useApi.js';
import { StatusBadge, PriorityBadge, SlaChip, SentimentChip } from '../../components/ui/StatusBadge.js';
import { ErrorState } from '../../components/ui/EmptyState.js';
import { RobotEmptyState } from '../../components/ui/RobotEmptyState.js';
import { TableRowSkeleton } from '../../components/ui/Skeleton.js';
import { relativeTime } from '../../lib/format.js';
import type { Ticket } from '../../api/types.js';
import { AssignTechDialog } from './AssignTechDialog.js';

interface Facets {
  statuses: Array<{ name: string; count: number }>;
  boards: Array<{ name: string; count: number }>;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function TicketsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Filters live in the URL so dashboard links (?unassigned=true, ?sla=risk) work.
  const statusFilter = params.get('status') ?? 'all';
  const boardFilter = params.get('board') ?? 'all';
  const unassignedOnly = params.get('unassigned') === 'true';
  const slaRiskOnly = params.get('sla') === 'risk';
  const showClosed = params.get('closed') === 'true';
  const [search, setSearch] = React.useState(params.get('q') ?? '');
  const debouncedSearch = useDebounced(search.trim(), 300);

  function setParam(key: string, value: string | null) {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === null || value === '' || value === 'all') next.delete(key); else next.set(key, value);
      return next;
    }, { replace: true });
  }

  React.useEffect(() => { setParam('q', debouncedSearch || null); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debouncedSearch]);

  // Server-side filtering over the whole tenant (not just a client-side page).
  const listUrl = React.useMemo(() => {
    const q = new URLSearchParams({ limit: '100', closed: showClosed ? 'true' : 'false' });
    if (statusFilter !== 'all') q.set('status', statusFilter);
    if (boardFilter !== 'all') q.set('board', boardFilter);
    if (unassignedOnly) q.set('unassigned', 'true');
    if (slaRiskOnly) { q.set('sla', 'risk'); q.set('sort', 'sla'); }
    if (debouncedSearch) q.set('q', debouncedSearch);
    return `/api/v1/tickets?${q.toString()}`;
  }, [statusFilter, boardFilter, unassignedOnly, slaRiskOnly, showClosed, debouncedSearch]);

  const { data, loading, error, refetch } = useGet<{ items: Ticket[]; total: number; nextCursor?: string }>(listUrl);
  const { data: facets, refetch: refetchFacets } = useGet<Facets>(`/api/v1/tickets/facets?closed=${showClosed ? 'true' : 'false'}`);
  const [more, setMore] = React.useState<Ticket[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [assignOpen, setAssignOpen] = React.useState(false);
  const [notice, setNotice] = React.useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  React.useEffect(() => { setMore([]); setCursor(data?.nextCursor); setSelected(new Set()); }, [data]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const r = await fetch(`${listUrl}&cursor=${encodeURIComponent(cursor)}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const page = (await r.json()) as { items: Ticket[]; nextCursor?: string };
      setMore(prev => [...prev, ...page.items]);
      setCursor(page.nextCursor);
    } catch (e) {
      setNotice({ kind: 'error', text: `Could not load more tickets: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setLoadingMore(false);
    }
  }

  const tickets = React.useMemo(() => [...(data?.items ?? []), ...more], [data, more]);

  const boards = React.useMemo(() => {
    const names = (facets?.boards ?? []).map(b => b.name);
    if (boardFilter !== 'all' && !names.includes(boardFilter)) names.push(boardFilter);
    return ['all', ...names];
  }, [facets, boardFilter]);

  const statuses = React.useMemo(() => {
    const names = (facets?.statuses ?? []).map(x => x.name);
    if (statusFilter !== 'all' && !names.includes(statusFilter)) names.push(statusFilter);
    return names;
  }, [facets, statusFilter]);

  const filtered = React.useMemo(() => {
    let items = tickets;
    // Sort: critical first, then warning, then by updated
    return [...items].sort((a, b) => {
      const riskOrder = { critical: 0, warning: 1, ok: 2 };
      const ra = riskOrder[a.slaRisk] - riskOrder[b.slaRisk];
      if (ra !== 0) return ra;
      if (slaRiskOnly) return new Date(a.slaDeadline).getTime() - new Date(b.slaDeadline).getTime();
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [tickets, slaRiskOnly]);

  function openTicket(id: string) {
    try {
      sessionStorage.setItem('tsc:ticketNav', JSON.stringify({ ids: filtered.map(t => t.id), at: Date.now() }));
    } catch {
      // Storage can be unavailable; detail navigation simply stays hidden.
    }
    navigate(`/tickets/${id}`);
  }

  // Keyboard navigation (j/k)
  React.useEffect(() => {
    function handler(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (assignOpen) return;
      if (e.key === 'j') setActiveIdx(i => Math.min(i + 1, filtered.length - 1));
      if (e.key === 'k') setActiveIdx(i => Math.max(i - 1, 0));
      if (e.key === 'o' && filtered[activeIdx]) openTicket(filtered[activeIdx].id);
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filtered, activeIdx, assignOpen]);

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(t => t.id)));
    }
  }

  /** Bulk assign: POST /api/v1/tickets/:id/assign per ticket (CW owner patch + audit). */
  async function bulkAssign(techId: string) {
    const ids = [...selected];
    const failures: string[] = [];
    for (const id of ids) {
      try {
        await apiPost(`/api/v1/tickets/${id}/assign`, { techId });
      } catch (e) {
        const t = tickets.find(x => x.id === id);
        failures.push(`#${t?.cwId ?? id}: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    const ok = ids.length - failures.length;
    if (ok === 0) throw new Error(failures[0] ?? 'Assignment failed');
    setAssignOpen(false);
    setNotice(failures.length
      ? { kind: 'error', text: `Assigned ${ok} of ${ids.length}. ${failures.slice(0, 3).join(' · ')}` }
      : { kind: 'ok', text: `Assigned ${ok} ticket${ok === 1 ? '' : 's'} in ConnectWise.` });
    refetch();
    refetchFacets();
  }

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const anyFilter = !!debouncedSearch || statusFilter !== 'all' || boardFilter !== 'all' || unassignedOnly || slaRiskOnly;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-5 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-[hsl(var(--content))]">Tickets</h1>
            <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} ${showClosed ? 'closed' : 'open'} tickets${data?.total && data.total !== filtered.length ? ` of ${data.total}` : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800">
                <span className="text-sm font-medium text-brand-700 dark:text-brand-300">{selected.size} selected</span>
                <button
                  type="button"
                  onClick={() => setAssignOpen(true)}
                  className="text-xs text-brand-600 hover:text-brand-800 dark:text-brand-400 font-medium"
                >
                  Bulk assign
                </button>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-brand-600 hover:text-brand-800 dark:text-brand-400 font-medium"
                >
                  Clear
                </button>
              </div>
            )}
            <button
              onClick={() => { refetch(); refetchFacets(); }}
              className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm text-[hsl(var(--content-subtle))] hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Refresh
            </button>
          </div>
        </div>

        {notice && (
          <div
            role={notice.kind === 'error' ? 'alert' : 'status'}
            className={cn(
              'mb-3 flex items-start justify-between gap-3 rounded-lg px-3 py-2 text-sm',
              notice.kind === 'error'
                ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
            )}
          >
            <span>{notice.text}</span>
            <button type="button" onClick={() => setNotice(null)} className="text-xs underline shrink-0">Dismiss</button>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <label htmlFor="ticket-search" className="sr-only">Search tickets</label>
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--content-subtle))] pointer-events-none" aria-hidden="true" />
            <input
              id="ticket-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search summary, company, #id…"
              className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content))] placeholder:text-[hsl(var(--content-placeholder))] outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent w-56"
            />
          </div>

          {/* Status filter: live ConnectWise statuses of open tickets */}
          <label htmlFor="status-filter" className="sr-only">Filter by status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={e => setParam('status', e.target.value)}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="all">All statuses</option>
            {statuses.map(st => {
              const n = facets?.statuses.find(x => x.name === st)?.count;
              return <option key={st} value={st}>{st}{n !== undefined && !showClosed ? ` (${n})` : ''}</option>;
            })}
          </select>

          {/* Board filter */}
          <label htmlFor="board-filter" className="sr-only">Filter by board</label>
          <select
            id="board-filter"
            value={boardFilter}
            onChange={e => setParam('board', e.target.value)}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500"
          >
            {boards.map(b => <option key={b} value={b}>{b === 'all' ? 'All boards' : b}</option>)}
          </select>

          {/* Toggles */}
          {([
            { key: 'unassigned', on: unassignedOnly, label: 'Unassigned only', dot: 'bg-amber-500', onCls: 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300' },
            { key: 'sla', on: slaRiskOnly, label: 'SLA at risk', dot: 'bg-red-500', onCls: 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300', value: 'risk' },
            { key: 'closed', on: showClosed, label: 'Closed tickets', dot: 'bg-slate-500', onCls: 'bg-slate-100 border-slate-300 text-slate-800 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200' },
          ] as Array<{ key: string; on: boolean; label: string; dot: string; onCls: string; value?: string }>).map(tg => (
            <button
              key={tg.key}
              type="button"
              aria-pressed={tg.on}
              onClick={() => setParam(tg.key, tg.on ? null : (tg.value ?? 'true'))}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                tg.on
                  ? tg.onCls
                  : 'border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-gray-100 dark:hover:bg-gray-800/60'
              )}
            >
              {tg.label}
              {tg.on && <span className={cn('h-2 w-2 rounded-full', tg.dot)} aria-hidden="true" />}
            </button>
          ))}
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="px-6 py-1.5 bg-[hsl(var(--surface-subtle))] border-b border-[hsl(var(--border))] text-[11px] text-[hsl(var(--content-placeholder))] hidden sm:flex items-center gap-3">
        <span><kbd className="border border-[hsl(var(--border))] rounded px-1 font-mono">j</kbd>/<kbd className="border border-[hsl(var(--border))] rounded px-1 font-mono">k</kbd> navigate</span>
        <span><kbd className="border border-[hsl(var(--border))] rounded px-1 font-mono">o</kbd> open</span>
        <span className="ml-auto">Click row to open</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {error ? (
          <ErrorState message={error} onRetry={refetch} />
        ) : loading ? (
          Array.from({ length: 8 }).map((_, i) => <TableRowSkeleton key={i} cols={7} />)
        ) : filtered.length === 0 ? (
          <RobotEmptyState
            title={anyFilter ? 'No tickets found' : showClosed ? 'No closed tickets synced' : 'All caught up!'}
            description={anyFilter ? 'Try adjusting your filters or search.' : 'Tickets appear here after the ConnectWise sync runs.'}
          />
        ) : (
          <>
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">Tickets</caption>
            <thead>
              <tr className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800/80 border-b-2 border-[hsl(var(--border-strong))] text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide select-none">
                <th scope="col" className="w-8 px-4 py-2.5">
                  <button
                    type="button"
                    onClick={toggleAll}
                    aria-pressed={allSelected}
                    aria-label={allSelected ? 'Deselect all tickets' : 'Select all tickets'}
                    className="flex items-center justify-center h-4 w-4 rounded border border-[hsl(var(--border))] hover:border-brand-500 transition-colors"
                  >
                    {allSelected && <CheckSquare className="h-3.5 w-3.5 text-brand-600" aria-hidden="true" />}
                  </button>
                </th>
                <th scope="col" className="w-14 px-2 py-2.5">Priority</th>
                <th scope="col" className="px-2 py-2.5 min-w-[16rem]">Summary</th>
                <th scope="col" className="w-28 px-2 py-2.5 hidden sm:table-cell">Status</th>
                <th scope="col" className="w-36 px-2 py-2.5 hidden lg:table-cell">Company</th>
                <th scope="col" className="w-32 px-2 py-2.5 hidden xl:table-cell">Owner</th>
                <th scope="col" className="w-24 px-2 py-2.5 hidden sm:table-cell">SLA</th>
                <th scope="col" className="w-28 px-2 py-2.5 hidden xl:table-cell">Sentiment</th>
                <th scope="col" className="w-20 px-2 py-2.5 hidden lg:table-cell">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ticket, idx) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  selected={selected.has(ticket.id)}
                  active={idx === activeIdx}
                  onSelect={() => toggleSelect(ticket.id)}
                  onClick={() => openTicket(ticket.id)}
                  onFocus={() => setActiveIdx(idx)}
                />
              ))}
            </tbody>
          </table>
          {cursor && (
            <div className="flex justify-center py-4">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm text-[hsl(var(--content-subtle))] hover:bg-gray-100 dark:hover:bg-gray-800/60 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
          </>
        )}
      </div>

      <AssignTechDialog
        open={assignOpen}
        title={`Assign ${selected.size} ticket${selected.size === 1 ? '' : 's'}`}
        description="Sets the ConnectWise owner and adds a [Dispatch] internal note. Boards in shadow mode are read-only."
        onClose={() => setAssignOpen(false)}
        onConfirm={techId => bulkAssign(techId)}
      />
    </div>
  );
}

function TicketRow({
  ticket, selected, active, onSelect, onClick, onFocus,
}: {
  ticket: Ticket;
  selected: boolean;
  active: boolean;
  onSelect: () => void;
  onClick: () => void;
  onFocus: () => void;
}) {
  return (
    <tr
      tabIndex={0}
      onClick={onClick}
      onFocus={onFocus}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      className={cn(
        'border-b border-[hsl(var(--border))] cursor-pointer transition-colors select-none',
        active && !selected
          ? 'shadow-[inset_2px_0_0_0_hsl(var(--border-focus))]'
          : selected
          ? 'bg-[hsl(var(--surface-subtle))]'
          : 'hover:bg-gray-100 dark:hover:bg-gray-800/60',
        ticket.slaRisk === 'critical' && 'border-l-2 border-l-red-500',
        ticket.slaRisk === 'warning' && 'border-l-2 border-l-amber-400',
      )}
      aria-selected={selected}
    >
      {/* Checkbox */}
      <td className="px-4 py-3">
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={`Select ticket ${ticket.cwId}`}
          onClick={e => { e.stopPropagation(); onSelect(); }}
          className={cn(
            'flex h-4 w-4 rounded border transition-colors items-center justify-center',
            selected ? 'bg-brand-600 border-brand-600' : 'border-[hsl(var(--border))] hover:border-brand-500'
          )}
        >
          {selected && <span className="text-white text-[10px]" aria-hidden="true">✓</span>}
        </button>
      </td>

      {/* Priority */}
      <td className="px-2 py-3">
        <PriorityBadge priority={ticket.priority} />
      </td>

      {/* Summary: has a floor so the secondary columns can never squeeze it to a few characters */}
      <td className="px-2 py-3 min-w-[16rem]">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[hsl(var(--content))] truncate">{ticket.summary}</span>
          {ticket.unassigned && (
            <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Unassigned</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-[hsl(var(--content-placeholder))]">#{ticket.cwId}</span>
          <span className="text-xs text-[hsl(var(--content-placeholder))] hidden sm:inline">· {ticket.board}</span>
        </div>
      </td>

      {/* Status */}
      <td className="px-2 py-3 hidden sm:table-cell">
        <StatusBadge label={ticket.status} />
      </td>

      {/* Company */}
      <td className="px-2 py-3 hidden lg:table-cell">
        <span className="text-sm text-[hsl(var(--content-subtle))] truncate block">{ticket.company}</span>
      </td>

      {/* Owner */}
      <td className="px-2 py-3 hidden xl:table-cell">
        {ticket.owner ? (
          <span className="text-sm text-[hsl(var(--content-subtle))] truncate block">{ticket.owner.split(' ')[0]}</span>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Unassigned</span>
        )}
      </td>

      {/* SLA */}
      <td className="px-2 py-3 hidden sm:table-cell">
        <SlaChip risk={ticket.slaRisk} deadline={ticket.slaDeadline} />
      </td>

      {/* Sentiment */}
      <td className="px-2 py-3 hidden xl:table-cell">
        {ticket.sentiment !== undefined && <SentimentChip score={ticket.sentiment} />}
      </td>

      {/* Updated */}
      <td className="px-2 py-3 hidden lg:table-cell">
        <span className="text-xs text-[hsl(var(--content-placeholder))]">{relativeTime(ticket.updatedAt)}</span>
      </td>
    </tr>
  );
}
